import React, { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Logout } from '~/Components/Icons/Logout';
import { useProviderSelection, IProviderFilter } from '.';
import { FitoListReactQuery } from '~/Components';
import { makeStyles } from '~/UIKit/Style';
import { useLocalStorage } from '~/Hooks';
import { IStudio } from '~/Models/DTO/Studio.model';
import { Button, Typography, OutlinedInput, InputAdornment, Select, MenuItem } from '~/UIKit';
import {
  FONT_SIZE_LARGER,
  FONT_SIZE_MEDIUM_CONFUSED,
  FONT_SIZE_INBETWEEN,
} from '~/Constants/UI.constants';
import { Search } from '@mui/icons-material';
import {
  DARKER_GREY,
  VERTICAL_SEPARATOR,
  combinedColor,
  PLAIN_GREY,
  OPACITY_MEDIUM,
} from '~/Constants/Colors.constants';
import { debounce } from 'lodash-es';
export const ProviderSelection = () => {
  const { classes } = useStyles();
  const { t } = useTranslation();
  const { logout } = useLocalStorage();
  const [searchText, setSearchText] = useState('');
  const [selectType, onSelectChange] = useState('name');
  const updateSearch = useCallback(debounce(setSearchText, 500), []);

  const {
    setItemValues,
    searchTypes,
    handleAddStudio,
    isSuperAdmin,
    tableDefinition,
    useRequestProvidersSearch,
    restProps,
  } = useProviderSelection({ searchText });
  const [defaultQuery, setDefaultQuery] = useState({
    name: '',
    page: 0,
    pageSize: 20,
  } as IProviderFilter);

  useEffect(() => {
    setDefaultQuery({ [selectType]: searchText, page: 0, pageSize: 20 });
  }, [selectType, searchText]);

  return (
    <div className={classes.container}>
      <div className={classes.columnView}>
        <div className={classes.titleWrapper}>
          <Typography className={classes.title}>{t('app.select.studio')}</Typography>
          <div className={classes.rowView}>
            {isSuperAdmin && (
              <Button
                variant="contained"
                color="primary"
                size="medium"
                onClick={() => handleAddStudio()}
              >
                {`+ ${t('app.add')} ${t('app.studio')}`}
              </Button>
            )}
            <Button size="medium" startIcon={<Logout color="primary" />} onClick={logout}>
              {t('app.logout')}
            </Button>
          </div>
        </div>
        <div className={classes.rowView}>
          <div className={classes.selectContainer}>
            <Select
              newDesign
              onChange={(ev) => onSelectChange(ev.target.value)}
              value={selectType}
              className={classes.sortSelect}
            >
              {searchTypes.map((item) => (
                <MenuItem key={item.value} value={item.value} className={classes.menuItems}>
                  {t(item.label)}
                </MenuItem>
              ))}
            </Select>
          </div>
          <div className={classes.searchContainer}>
            <OutlinedInput
              className={classes.searchInput}
              onChange={(e) => updateSearch(e.currentTarget.value)}
              placeholder={t('app.search.provider')}
              startAdornment={
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              }
            />
          </div>
        </div>
      </div>
      <div className={classes.appContent} id="private_layout_details">
        <div className={classes.content}>
          <FitoListReactQuery<IStudio>
            keyOfId="uuid"
            queryResults={useRequestProvidersSearch}
            defaultQuery={defaultQuery}
            appliedFilters={{}}
            setItemValues={setItemValues}
            tableDefinition={tableDefinition}
            {...restProps}
          />
        </div>
      </div>
    </div>
  );
};

const useStyles = makeStyles(
  (theme) => ({
    appContent: {
      display: 'flex',
      flexDirection: 'row',
      height: `calc(100vh - ${122}px - 3px)`,
      [theme.breakpoints.up('sm')]: {
        height: `calc(100vh - ${130}px - 1px)`,
      },
      overflowY: 'auto',
    },
    content: {
      flexGrow: 1,
      height: 'fit-content',
      minHeight: '100%',
      width: '100vw',
      display: 'flex',
      flexDirection: 'column',
      maxWidth: '100%',
      [theme.breakpoints.up('md')]: {
        overflowY: 'auto',
        padding: '0 15px',
      },
    },
    container: {
      display: 'flex',
      flexDirection: 'column',
      overflow: 'auto',
      [theme.breakpoints.up('md')]: {
        margin: '0 30px',
      },
    },
    title: {
      margin: '30px 0 15px',
      paddingLeft: theme.spacing(2),
      fontSize: FONT_SIZE_LARGER,
      letterSpacing: '0.5px',
      lineHeight: '32px',
      fontWeight: 'bold',
    },
    titleWrapper: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'baseline',
      marginRight: 15,
    },
    columnView: {
      display: 'flex',
      flexDirection: 'column',
    },
    searchInput: {
      height: 28,
      marginTop: 1,
      width: '100%',
      borderRadius: '25px',
      color: DARKER_GREY,
      fontSize: '12px',
      lineHeight: '16px',
      padding: '5px',
      '& svg': {
        fontSize: FONT_SIZE_MEDIUM_CONFUSED,
        color: VERTICAL_SEPARATOR,
      },
      '& input': {
        letterSpacing: 0.5,
      },
      '& fieldset': {
        borderColor: combinedColor(PLAIN_GREY, OPACITY_MEDIUM),
      },
    },
    sortSelect: {
      width: 150,
      fontSize: '12px',
      fontWeight: 'bold',
      color: DARKER_GREY,
      borderRadius: '25px',
      border: `1px solid ${combinedColor(PLAIN_GREY, OPACITY_MEDIUM)}`,
      lineHeight: '16px',
      padding: '0px 0px 0px 10px',
      '& svg': {
        fontSize: FONT_SIZE_MEDIUM_CONFUSED,
        color: VERTICAL_SEPARATOR,
        position: 'relative',
      },
      '& input': {
        padding: 0,
        width: 'fill-available',
        letterSpacing: 0.5,
      },
      '& fieldset': {
        borderColor: combinedColor(PLAIN_GREY, OPACITY_MEDIUM),
      },
    },
    menuItems: {
      fontSize: FONT_SIZE_INBETWEEN,
    },
    selectContainer: {
      display: 'flex',
      width: 150,
    },
    searchContainer: {
      display: 'flex',
      marginLeft: 15,
      width: '100%',
      [theme.breakpoints.up('md')]: {
        width: 395,
      },
    },
    rowView: {
      display: 'flex',
      flexDirection: 'row',
      justifyContent: 'space-between',
      margin: 0,
      padding: '0 15px',
      [theme.breakpoints.up('md')]: {
        justifyContent: 'flex-start',
        margin: '8px 0',
      },
    },
  }),
  {
    name: 'ProviderSelection',
  },
);
